import {BarChartConfig, LineChartConfig, TreemapWidgetConfig} from "./components/content/charts";
import {TableConfig, TableDataPoint} from "./components/content/table";
import {InfoBlockConfig, InfoBlockDataPoint} from "./components/content/info-block";
import {SearchDataSettingsOrderBy} from "../../../../../hooks/use-search";
import {Breakpoint} from "@mui/system";

/**
 * Enum for different types of dashboard widgets
 */
export enum DashboardWidgetType {
    table = 'table',
    lineChart = 'line-chart',
    barChart = 'bar-chart',
    infoBlock = 'info-block',
    treeMap = 'tree-map',
    treeMapOld = 'treemap',
}

export interface DashboardWidgetVariantInfo {
    variant?: string;
    isDefaultVariant: boolean;
    type: DashboardWidgetType;
}

export interface DashboardEntry {
    id: number;
    title: string;
    isDefault: boolean;
    ownerName: string;
    isOwnedByMe: boolean;
    uiComponentData?: string;
    newWidgets: DashboardWidgetEntry[];
    groupIds: DashboardGroupEntity['id'][];
}

export interface DashboardGroupEntity {
    id: number;
    name: string;
}

export interface DashboardWidgetEntryParameter {
    name: typeof DashboardFilterParameter[keyof typeof DashboardFilterParameter];
    title: string;
    type: string;
    description: string;
}

export interface DashboardWidgetEntryInner {
    id: string;
    title: string;
    description?: string;
    category: string;
    parameters: DashboardWidgetEntryParameter[];
}

export interface DashboardWidgetEntry extends DashboardWidgetEntryInner {
    connectionId: string;
    customTitle?: string;
    customFilter?: string;
}

/**
 * Chart data type interfaces
 */
export interface DashboardChartDataPoint {
    [key: string]: string | number;
}

export interface DashboardWidgetLineChartResponse {
    data: DashboardChartDataPoint[];
}

export interface DashboardWidgetBarChartResponse {
    data: DashboardChartDataPoint[];
}

export interface DashboardWidgetTableResponse {
    data: TableDataPoint[];
}

export interface DashboardWidgetInfoResponse {
    data: InfoBlockDataPoint[];
}

/**
 * Union type for all possible widget data types
 */
export type DashboardWidgetData = (
    | DashboardWidgetTableResponse
    | DashboardWidgetLineChartResponse
    | DashboardWidgetBarChartResponse
    | DashboardWidgetInfoResponse
    );

export type DashboardWidgetStaticDataConfig = BarChartConfig | LineChartConfig | TableConfig | InfoBlockConfig | TreemapWidgetConfig;

export type DashboardWidgetConfiguration = Partial<Record<DashboardWidgetType, DashboardWidgetStaticData[]>>;

export interface DashboardWidgetStaticData {
    config: DashboardWidgetStaticDataConfig;
    variant?: string;
}

export interface DashboardWidgetLayoutConfig {
    orderIndex?: number;
    size?: { [key in Breakpoint]?: number };
    type: DashboardWidgetType;
    underlyingDataVisibility?: Record<string, boolean>;
    variant?: string;
}

export interface AugmentedDashboardWidgetEntry extends Omit<DashboardWidgetEntry, 'filters'>, DashboardWidgetLayoutConfig, SavedUiComponentStateWidgetConfig {
    configuration?: DashboardWidgetConfiguration;
    filters?: DashboardViewFilterSectionFilters;
}

export interface DashboardWidgetSizing {
    size: { [key in Breakpoint]?: number }
    minSize: { [key in Breakpoint]?: number }
}

export interface SavedUiComponentStateWidgetConfig {
    connectionId: string;
    orderBy?: SearchDataSettingsOrderBy;
}

export interface DashboardWidgetOrderByField {
    name: string;
    title: string;
}

/**
 * Enum for all possible dashboard filter parameters
 */
export enum DashboardFilterParameter {
    CurrencyCode = 'CurrencyCode',
    CustomerIds = 'CustomerIds',
    ExcludeCustomerIds = 'ExcludeCustomerIds',
    PartNumbers = 'PartNumbers',
    PartNumberPrefix = 'PartNumberPrefix',
    ExcludePartNumbers = 'ExcludePartNumbers',
    StartDate = 'StartDate',
    EndDate = 'EndDate',
    Industry = 'Industry',
    PartCategories = 'PartCategories',
    TerritoryCodes = 'TerritoryCodes',
    PSR_ReasonIds = 'PSR_ReasonIds',
    PSR_StatusIds = 'PSR_StatusIds',
    PSR_Types = 'PSR_Types',
    PSR_VendorStatus = 'PSR_VendorStatus',
    WarehouseCodes = 'WarehouseCodes',
    SalesWarehouseCodes = 'SalesWarehouseCodes',
    OperatorIds = 'OperatorIds',
    PSR_ExcludeReasonIds = 'PSR_ExcludeReasonIds',
    HasCredited = 'HasCredited',
    ProvinceCodes = 'ProvinceCodes',
    Cities = 'Cities',
    AccountManagersCodes = 'AccountManagersCodes',
    ArTransactionTypes = 'ArTransactionTypes',
    VendorNumbers = 'VendorNumbers',
    // TODO: Add new Filter Parameters here
}

/**
 * A widget-level filter that must stay applied (cannot follow dashboard-only filters).
 * `filterName` is a value from the widget's parameter pool (`DashboardFilterParameter`).
 */
export interface WidgetForcedFilterRequirement {
    filterName: DashboardFilterParameter;
    isRequired?: boolean;
    requiredMinCount?: number;
    requiredMaxCount?: number;
}


/**
 * Interface defining the structure of filter values used in the dashboard view
 * All fields are optional to allow partial filter application
 */
export interface DashboardViewFilterSectionFilters {
    dateRangeValue?: string; // CommitDateRangeArg
    // industry
    industry?: string;
    // currency
    currency?: string;
    // part numbers
    excludePartNumbers?: string[];
    partNumbers?: string[]; // JSON
    partNumberPrefix?: string;
    // customers
    excludeCustomers?: string[]; // JSON
    customers?: string[]; // JSON
    // part categories
    categories?: string[]; // JSON
    // territories
    territoryCodes?: string[];
    // operators
    operators?: string[]; // JSON
    // part support related filters
    partSupportReasons?: string[]; // JSON
    partSupportStatuses?: string[]; // JSON
    partSupportTypes?: string[]; // JSON
    partSupportVendorStatuses?: string[]; // JSON
    partSupportExcludeReasons?: string[]; // JSON
    hasCredited?: boolean;
    mergeWithParentFilters?: boolean; // Whether to merge with parent dashboard filters
    // warehouses
    warehouses?: string[]; // JSON
    // sales warehouses (API parameter SalesWarehouseCodes)
    salesWarehouses?: string[]; // JSON
    // Province and City
    provinces?: string[]; // JSON
    cities?: string[];
    // Sales Persons
    accountManagers?: string[]; // JSON
    // AR Transaction Types
    arTransactionTypes?: string[]; // JSON
    // Vendors (codes only; API parameter VendorNumbers)
    vendorNumbers?: string[];
    // TODO: Add new filters here
}